import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getRandomLoadingMessage } from '../../utils/loadingMessages';
import api from '../../services/api';

const API_URL = 'accounts/admin/inspect/jobs/';
const REFRESH_INTERVAL = 30000;

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 60) return Math.round(diff) + 's ago';
  if (diff < 3600) return Math.round(diff / 60) + 'm ago';
  if (diff < 86400) return Math.round(diff / 3600) + 'h ago';
  return Math.round(diff / 86400) + 'd ago';
}

function uptimeLabel(startedAt) {
  if (!startedAt) return '';
  const diff = Math.floor((Date.now() - new Date(startedAt)) / 1000);
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  return `up ${h}h ${m}m`;
}

function parseLogLine(line) {
  // Expect lines like: [2026-03-11 23:04:00] message here (timestamps are UTC)
  const m = line.match(/^\[(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})\]\s*(.*)/);
  if (m) {
    // Convert UTC timestamp to EST with AM/PM
    const utcStr = m[1].replace(' ', 'T') + 'Z';
    const date = new Date(utcStr);
    const formatted = date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    return { ts: formatted, msg: m[2] };
  }
  return { ts: null, msg: line };
}

export default function AdminJobs() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [containerBusy, setContainerBusy] = useState({});   // { [name]: 'restart'|'stop'|'start'|null }
  const [containerMsg, setContainerMsg] = useState({});     // { [name]: { ok, err } }
  const intervalRef = useRef(null);

  // ── Job-level state ─────────────────────────────────────────────────────────
  const [jobLogOpen, setJobLogOpen] = useState(null);        // which job's log is open
  const [jobLogData, setJobLogData] = useState({});          // { [jobName]: { lines, loading, err } }
  const [jobRunBusy, setJobRunBusy] = useState({});          // { [jobName]: true|false }
  const [jobRunMsg, setJobRunMsg] = useState({});            // { [jobName]: { ok, msg, err } }
  const logBoxRefs = useRef({});                             // refs to log box divs for auto-scroll
  const activityLogRef = useRef(null);                       // ref to activity log box

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(API_URL);
      setData(res.data);
      setLastFetched(new Date());
      setError(null);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        setError('Session expired. Please log in again.');
      } else {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login?next=/admin/jobs'); return; }
    if (user && !user.is_staff && !user.is_superuser) { navigate('/'); return; }
    fetchData();
    intervalRef.current = setInterval(fetchData, REFRESH_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [isAuthenticated, user, fetchData, navigate]);

  // Auto-scroll log boxes to bottom when logs load
  useEffect(() => {
    Object.entries(jobLogData).forEach(([jobName, state]) => {
      if (!state.loading && state.lines && state.lines.length > 0) {
        const ref = logBoxRefs.current[jobName];
        if (ref) {
          // Small delay to ensure DOM is updated
          setTimeout(() => {
            ref.scrollTop = ref.scrollHeight;
          }, 0);
        }
      }
    });
  }, [jobLogData]);

  const handleContainerAction = async (name, actionName) => {
    const token = localStorage.getItem('access_token') ||
                  localStorage.getItem('accessToken') ||
                  sessionStorage.getItem('accessToken');
    setContainerBusy(prev => ({ ...prev, [name]: actionName }));
    setContainerMsg(prev => ({ ...prev, [name]: null }));
    try {
      const res = await fetch('/api/accounts/admin/container-action/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ name, action: actionName }),
      });
      const json = await res.json();
      if (json.ok) {
        setContainerMsg(prev => ({ ...prev, [name]: { ok: true } }));
        setTimeout(() => fetchData(), 1500);  // refresh status after action
      } else {
        setContainerMsg(prev => ({ ...prev, [name]: { err: json.error || 'Failed' } }));
      }
    } catch (e) {
      setContainerMsg(prev => ({ ...prev, [name]: { err: 'Network error' } }));
    } finally {
      setContainerBusy(prev => ({ ...prev, [name]: null }));
    }
  };

  // ── Job helpers ─────────────────────────────────────────────────────────────
  const getToken = () =>
    localStorage.getItem('access_token') ||
    localStorage.getItem('accessToken') ||
    sessionStorage.getItem('accessToken');

  const fetchJobLogs = async (jobName) => {
    // Toggle closed if already open
    if (jobLogOpen === jobName) {
      setJobLogOpen(null);
      return;
    }
    setJobLogOpen(jobName);
    setJobLogData(prev => ({ ...prev, [jobName]: { lines: [], loading: true, err: null } }));
    try {
      const token = getToken();
      const res = await fetch(
        `/api/accounts/admin/job-logs/?job=${encodeURIComponent(jobName)}&lines=300`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include' }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setJobLogData(prev => ({ ...prev, [jobName]: { lines: json.lines || [], loading: false, err: null } }));
    } catch (e) {
      setJobLogData(prev => ({ ...prev, [jobName]: { lines: [], loading: false, err: e.message } }));
    }
  };

  const downloadJobLogs = async (jobName) => {
    try {
      const token = getToken();
      const res = await fetch(
        `/api/accounts/admin/job-logs/?job=${encodeURIComponent(jobName)}&lines=2000&filter=0&download=1`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include' }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pwhl-${jobName}.log`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Download failed: ${e.message}`);
    }
  };

  const handleJobRun = async (jobName) => {
    if (!window.confirm(`Trigger "${jobName}" manually now?`)) return;
    const token = getToken();
    setJobRunBusy(prev => ({ ...prev, [jobName]: true }));
    setJobRunMsg(prev => ({ ...prev, [jobName]: null }));
    try {
      const res = await fetch('/api/accounts/admin/job-run/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ job: jobName }),
      });
      const json = await res.json();
      if (json.ok) {
        setJobRunMsg(prev => ({ ...prev, [jobName]: { ok: true, msg: json.message } }));
      } else {
        setJobRunMsg(prev => ({ ...prev, [jobName]: { ok: false, err: json.error || 'Failed' } }));
      }
    } catch (e) {
      setJobRunMsg(prev => ({ ...prev, [jobName]: { ok: false, err: 'Network error' } }));
    } finally {
      setJobRunBusy(prev => ({ ...prev, [jobName]: false }));
    }
  };

  if (!isAuthenticated) return null;
  if (error === 'admin_only') return <Navigate to="/" replace />;

  const status = data?.status || {};
  const jobsLog = data?.jobs_log || [];
  const dbLog = data?.db_log || [];
  const containers = status.containers || [];
  const jobs = status.jobs || [];

  // Combine and sort logs ascending by timestamp (oldest first)
  const allLogLines = [...jobsLog, ...dbLog]
    .map(parseLogLine)
    .filter(l => l.msg.trim())
    .sort((a, b) => {
      if (!a.ts && !b.ts) return 0;
      if (!a.ts) return 1;
      if (!b.ts) return -1;
      return a.ts.localeCompare(b.ts);
    })
    .slice(0, 100);

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>⚙️ Jobs &amp; Containers</h1>
          <p style={s.subtitle}>
            {lastFetched
              ? `Last updated ${timeAgo(lastFetched)}`
              : loading ? 'Loading…' : 'Never'}
          </p>
        </div>
        <button style={s.refreshBtn} onClick={fetchData} title="Refresh now">
          ↻ Refresh
        </button>
      </div>

      {loading && !data && (
        <div style={s.loading}>{getRandomLoadingMessage()}</div>
      )}

      {error && error !== 'admin_only' && (
        <div style={s.errorBox}>
          {error}
          {error.includes('Session expired') && (
            <> — <a href="/login?next=/admin/jobs" style={{ color: '#f87171', textDecoration: 'underline', cursor: 'pointer' }}>Log in again</a></>
          )}
        </div>
      )}

      {/* Status-file unavailable notice */}
      {status.error && (
        <div style={s.noticeBox}>
          📡 Status data not yet available — monitor scripts will populate this within 5 minutes.
        </div>
      )}

      {/* ── CONTAINERS ── */}
      {containers.length > 0 && (
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Containers</h2>
          <div style={s.containerGrid}>
            {containers.map(c => (
              <div
                key={c.name}
                style={{
                  ...s.containerCard,
                  ...(c.running ? {} : s.containerCardDown),
                }}
              >
                <div style={s.containerHeader}>
                  <span style={c.running ? s.dotGreen : s.dotRed} />
                  <span style={s.containerName}>{c.display}</span>
                  <span style={{ ...s.statusBadge, ...(c.running ? s.badgeGreen : s.badgeRed) }}>
                    {c.running ? 'Running' : 'Stopped'}
                  </span>
                </div>
                <div style={s.containerMeta}>
                  <span style={s.metaKey}>Status:</span>
                  <span style={s.metaVal}>{c.status || '—'}</span>
                </div>
                {c.running && c.started_at && (
                  <div style={s.containerMeta}>
                    <span style={s.metaKey}>Uptime:</span>
                    <span style={s.metaVal}>{uptimeLabel(c.started_at)}</span>
                  </div>
                )}
                <div style={{ ...s.containerMeta, marginTop: 2 }}>
                  <span style={s.metaDim}>{c.name}</span>
                </div>

                {/* ── Action buttons ── */}
                <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
                  {c.running ? (
                    <>
                      <button
                        style={{ ...s.actionBtn, ...s.actionBtnYellow }}
                        disabled={!!containerBusy[c.name]}
                        onClick={() => handleContainerAction(c.name, 'restart')}
                      >
                        {containerBusy[c.name] === 'restart' ? '…' : '↺ Restart'}
                      </button>
                      <button
                        style={{ ...s.actionBtn, ...s.actionBtnRed }}
                        disabled={!!containerBusy[c.name]}
                        onClick={() => handleContainerAction(c.name, 'stop')}
                      >
                        {containerBusy[c.name] === 'stop' ? '…' : '◼ Stop'}
                      </button>
                    </>
                  ) : (
                    <button
                      style={{ ...s.actionBtn, ...s.actionBtnGreen }}
                      disabled={!!containerBusy[c.name]}
                      onClick={() => handleContainerAction(c.name, 'start')}
                    >
                      {containerBusy[c.name] === 'start' ? '…' : '▶ Start'}
                    </button>
                  )}
                </div>
                {containerMsg[c.name]?.ok && (
                  <div style={{ fontSize: '0.75rem', color: '#4ade80', marginTop: '4px' }}>Done ✓</div>
                )}
                {containerMsg[c.name]?.err && (
                  <div style={{ fontSize: '0.75rem', color: '#f87171', marginTop: '4px' }}>{containerMsg[c.name].err}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── SCHEDULED JOBS ── */}
      {jobs.length > 0 && (
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Scheduled Jobs</h2>
          <div>
            {jobs.map(j => {
              const logState = jobLogData[j.name] || {};
              const isLogOpen = jobLogOpen === j.name;
              const runMsg = jobRunMsg[j.name];
              return (
                <div key={j.name}>
                  {/* ── Main row ── */}
                  <div
                    style={{
                      ...s.jobRow,
                      ...(j.status === 'stale' ? s.jobRowStale : {}),
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      gap: 10,
                    }}
                  >
                    {/* Top: name + badge */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                      <div style={s.jobLeft}>
                        <span style={s.jobName}>{j.display}</span>
                        <span style={s.jobSchedule}>{j.schedule}</span>
                      </div>
                      <div style={s.jobRight}>
                        <span style={s.jobAge}>
                          {j.last_run_at ? timeAgo(j.last_run_at) : '—'}
                        </span>
                        {j.status === 'ok' && (
                          <span style={{ ...s.jobBadge, ...s.badgeGreenSm }}>🟢 FRESH</span>
                        )}
                        {j.status === 'stale' && (
                          <span style={{ ...s.jobBadge, ...s.badgeYellowSm }}>🟡 STALE</span>
                        )}
                        {j.status === 'unknown' && (
                          <span style={{ ...s.jobBadge, ...s.badgeGraySm }}>⚪ UNKNOWN</span>
                        )}
                      </div>
                    </div>

                    {/* Bottom: action buttons */}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button
                        style={{ ...s.actionBtn, ...s.actionBtnBlue, ...(isLogOpen ? s.actionBtnBlueActive : {}) }}
                        onClick={() => fetchJobLogs(j.name)}
                      >
                        {isLogOpen ? '▲ Hide Logs' : '📋 View Logs'}
                      </button>
                      <button
                        style={{ ...s.actionBtn, ...s.actionBtnGray }}
                        onClick={() => downloadJobLogs(j.name)}
                      >
                        ⬇ Download Logs
                      </button>
                      <button
                        style={{ ...s.actionBtn, ...s.actionBtnGreen }}
                        disabled={!!jobRunBusy[j.name]}
                        onClick={() => handleJobRun(j.name)}
                      >
                        {jobRunBusy[j.name] ? '⏳ Running…' : '▶ Run Now'}
                      </button>
                    </div>

                    {/* Run Now feedback */}
                    {runMsg?.ok && (
                      <div style={{ fontSize: '0.75rem', color: '#4ade80' }}>✓ {runMsg.msg}</div>
                    )}
                    {runMsg?.err && (
                      <div style={{ fontSize: '0.75rem', color: '#f87171' }}>✗ {runMsg.err}</div>
                    )}
                  </div>

                  {/* ── Inline log panel ── */}
                  {isLogOpen && (
                    <div style={s.jobLogPanel}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {j.display} — Log ({logState.lines?.length || 0} lines)
                        </span>
                        <button style={{ ...s.actionBtn, ...s.actionBtnGray }} onClick={() => fetchJobLogs(j.name)}>
                          ↺ Refresh
                        </button>
                      </div>
                      <div
                        ref={el => { if (el) logBoxRefs.current[j.name] = el; }}
                        style={s.logBox}
                      >
                        {logState.loading && <div style={s.logEmpty}>{getRandomLoadingMessage()}</div>}
                        {logState.err && <div style={{ color: '#f87171' }}>Error: {logState.err}</div>}
                        {!logState.loading && !logState.err && (logState.lines || []).length === 0 && (
                          <div style={s.logEmpty}>No log entries found.</div>
                        )}
                        {!logState.loading && (logState.lines || []).map((line, i) => {
                          const parsed = parseLogLine(line);
                          return (
                            <div key={i} style={s.logLine}>
                              {parsed.ts && <span style={s.logTs}>[{parsed.ts}]</span>}
                              <span style={{
                                ...s.logMsg,
                                ...(line.toLowerCase().includes('stale') || line.toLowerCase().includes('fail') || line.toLowerCase().includes('error') ? { color: '#fca5a5' } : {}),
                                ...(line.toLowerCase().includes('ok') || line.toLowerCase().includes('healthy') || line.toLowerCase().includes('recovered') ? { color: '#86efac' } : {}),
                              }}>
                                {parsed.msg}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── ACTIVITY LOG ── */}
      <div style={s.section}>
        <h2 style={s.sectionTitle}>Activity Log</h2>
        <div ref={activityLogRef} style={s.logBox}>
          {allLogLines.length === 0 ? (
            <div style={s.logEmpty}>No log entries yet.</div>
          ) : (
            allLogLines.map((line, i) => (
              <div key={i} style={s.logLine}>
                {line.ts && <span style={s.logTs}>[{line.ts}]</span>}
                <span style={s.logMsg}>{line.msg}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    background: '#0d0d1a',
    color: '#e5e7eb',
    padding: '100px 24px 60px',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
  },
  loading: {
    textAlign: 'center',
    marginTop: 80,
    color: '#a855f7',
    fontSize: 18,
  },
  errorBox: {
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: 12,
    padding: '16px 24px',
    maxWidth: 600,
    margin: '0 auto 24px',
    color: '#fca5a5',
    fontSize: 14,
  },
  noticeBox: {
    background: 'rgba(168,85,247,0.08)',
    border: '1px solid rgba(168,85,247,0.25)',
    borderRadius: 12,
    padding: '16px 24px',
    maxWidth: 1200,
    margin: '0 auto 24px',
    color: '#d8b4fe',
    fontSize: 14,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 16,
    maxWidth: 1200,
    margin: '0 auto 32px',
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: '#fff',
    margin: 0,
  },
  subtitle: {
    color: '#9ca3af',
    margin: '4px 0 0',
    fontSize: 13,
  },
  refreshBtn: {
    background: 'rgba(168,85,247,0.15)',
    border: '1px solid rgba(168,85,247,0.35)',
    borderRadius: 8,
    color: '#d8b4fe',
    padding: '8px 18px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
  },
  section: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 14,
    padding: '20px 24px',
    maxWidth: 1200,
    margin: '0 auto 16px',
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: '#d1d5db',
    marginTop: 0,
    marginBottom: 16,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  containerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: 12,
  },
  containerCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10,
    padding: '14px 16px',
  },
  containerCardDown: {
    background: 'rgba(239,68,68,0.06)',
    border: '1px solid rgba(239,68,68,0.2)',
  },
  containerHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  dotGreen: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: '#22c55e',
    flexShrink: 0,
    boxShadow: '0 0 6px rgba(34,197,94,0.6)',
  },
  dotRed: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: '#ef4444',
    flexShrink: 0,
    boxShadow: '0 0 6px rgba(239,68,68,0.6)',
  },
  containerName: {
    fontWeight: 600,
    fontSize: 14,
    color: '#e5e7eb',
    flex: 1,
  },
  statusBadge: {
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 20,
    letterSpacing: '0.03em',
  },
  badgeGreen: {
    background: 'rgba(34,197,94,0.15)',
    color: '#86efac',
    border: '1px solid rgba(34,197,94,0.25)',
  },
  badgeRed: {
    background: 'rgba(239,68,68,0.15)',
    color: '#fca5a5',
    border: '1px solid rgba(239,68,68,0.25)',
  },
  containerMeta: {
    display: 'flex',
    gap: 6,
    fontSize: 12,
    marginBottom: 2,
  },
  metaKey: {
    color: '#6b7280',
  },
  metaVal: {
    color: '#d1d5db',
  },
  metaDim: {
    color: '#4b5563',
    fontSize: 11,
    fontFamily: 'monospace',
  },
  jobRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderRadius: 8,
    marginBottom: 8,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    gap: 12,
    flexWrap: 'wrap',
  },
  jobRowStale: {
    background: 'rgba(234,179,8,0.06)',
    border: '1px solid rgba(234,179,8,0.2)',
  },
  jobLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  jobName: {
    fontWeight: 600,
    fontSize: 14,
    color: '#e5e7eb',
  },
  jobSchedule: {
    fontSize: 12,
    color: '#6b7280',
  },
  jobRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  jobAge: {
    fontSize: 13,
    color: '#9ca3af',
  },
  jobBadge: {
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 10px',
    borderRadius: 20,
    letterSpacing: '0.04em',
  },
  badgeGreenSm: {
    background: 'rgba(34,197,94,0.12)',
    color: '#86efac',
    border: '1px solid rgba(34,197,94,0.2)',
  },
  badgeYellowSm: {
    background: 'rgba(234,179,8,0.12)',
    color: '#fde047',
    border: '1px solid rgba(234,179,8,0.25)',
  },
  badgeGraySm: {
    background: 'rgba(107,114,128,0.15)',
    color: '#9ca3af',
    border: '1px solid rgba(107,114,128,0.2)',
  },
  logBox: {
    background: '#0a0a14',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 8,
    padding: '14px 16px',
    maxHeight: 480,
    overflowY: 'auto',
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
    fontSize: 12,
    lineHeight: 1.6,
  },
  logEmpty: {
    color: '#4b5563',
    fontStyle: 'italic',
  },
  logLine: {
    display: 'flex',
    gap: 8,
    marginBottom: 2,
    wordBreak: 'break-word',
  },
  logTs: {
    color: '#4b5563',
    flexShrink: 0,
    userSelect: 'none',
  },
  logMsg: {
    color: '#d1d5db',
  },
  actionBtn: {
    padding: '4px 10px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '0.75rem',
    fontWeight: '600',
    cursor: 'pointer',
    opacity: 1,
    transition: 'opacity 0.15s',
  },
  actionBtnYellow: {
    background: 'rgba(234,179,8,0.18)',
    color: '#fde047',
    border: '1px solid rgba(234,179,8,0.3)',
  },
  actionBtnRed: {
    background: 'rgba(239,68,68,0.18)',
    color: '#fca5a5',
    border: '1px solid rgba(239,68,68,0.3)',
  },
  actionBtnGreen: {
    background: 'rgba(34,197,94,0.18)',
    color: '#86efac',
    border: '1px solid rgba(34,197,94,0.3)',
  },
  actionBtnBlue: {
    background: 'rgba(99,102,241,0.18)',
    color: '#a5b4fc',
    border: '1px solid rgba(99,102,241,0.3)',
  },
  actionBtnBlueActive: {
    background: 'rgba(99,102,241,0.35)',
    border: '1px solid rgba(99,102,241,0.6)',
  },
  actionBtnGray: {
    background: 'rgba(107,114,128,0.18)',
    color: '#d1d5db',
    border: '1px solid rgba(107,114,128,0.3)',
  },
  jobLogPanel: {
    background: 'rgba(0,0,0,0.25)',
    border: '1px solid rgba(99,102,241,0.2)',
    borderTop: 'none',
    borderRadius: '0 0 8px 8px',
    padding: '12px 16px 16px',
    marginBottom: 8,
    marginTop: -8,
  },
};
